import { format } from 'date-fns';
import { enqueueSnackbar } from 'notistack';
import { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Radio from '@mui/material/Radio';
import Stack from '@mui/material/Stack';
import { LoadingButton } from '@mui/lab';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import ToggleButton from '@mui/material/ToggleButton';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { useGetConfigByEntityTypeQuery } from 'src/services/custom-fields/custom-field.service';
import { CustomFieldType, CustomFieldEntityType } from 'src/services/custom-fields/custom-field.types';
import { useGetCallListsQuery, useAddCallListEntriesMutation, useGetCallListMembershipsQuery } from 'src/services/call-lists/call-lists.service';
import {
  CallList,
  FirstCallWhen,
  CallNumberType,
  CallListAddedFrom,
  CallListPersonType,
  CallListEntryStatus,
  AddCallListEntriesResult,
} from 'src/services/call-lists/call-lists.types';

import Iconify from 'src/components/iconify';

import CallListNameModal from './call-list-name-modal';
import CallListPeoplePicker, { PickedPerson } from '../components/call-list-people-picker';
import CallListOwnerPicker, { useIsCallListAdmin } from '../components/call-list-owner-picker';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  onClose: VoidFunction;
  personType: CallListPersonType;
  /** Who to add. Leave empty with `pickPeople` to choose them in the dialog. */
  ids: string[];
  /** Add people on the call list page: search for contacts or candidates in the dialog. */
  pickPeople?: boolean;
  addedFrom?: CallListAddedFrom;
  /** The person's name when adding one, for the dialog's header. */
  personName?: string;
  /** Preselect a list, e.g. the one open on the call list page. */
  defaultListId?: string;
  /** Admins: start on this team member's lists. */
  defaultOwnerId?: string | null;
  onSuccess?: (result: AddCallListEntriesResult) => void;
};

const FIRST_CALLS: FirstCallWhen[] = [FirstCallWhen.TODAY, FirstCallWhen.TOMORROW, FirstCallWhen.NEXT_WEEK, FirstCallWhen.DATE];

/**
 * Add one or many contacts or candidates to a call list (JT1-1827): which list, when the first call
 * is, which number to dial, and why. People already active on the list or marked do not call are
 * skipped by the server, and the toast says how many.
 */
export default function AddToCallListModal({ open, onClose, personType: givenType, ids: givenIds, pickPeople, addedFrom, personName, defaultListId, defaultOwnerId, onSuccess }: Props) {
  const { t } = useTranslate();
  const createModal = useBoolean();

  // JT1-1827: an admin can add people to a team member's list.
  const isAdmin = useIsCallListAdmin();
  const [forOwnerId, setForOwnerId] = useState<string | null>(null);
  const { currentData: lists } = useGetCallListsQuery({ ownerId: isAdmin ? forOwnerId : null }, { skip: !open });
  const [addEntries, { isLoading }] = useAddCallListEntriesMutation();

  const [listId, setListId] = useState<string | null>(null);
  const [firstCall, setFirstCall] = useState<FirstCallWhen>(FirstCallWhen.TODAY);
  const [firstCallDate, setFirstCallDate] = useState('');
  const [numberChoice, setNumberChoice] = useState<string>(CallNumberType.PHONE);
  const [otherNumber, setOtherNumber] = useState('');
  const [reason, setReason] = useState('');
  const [pickedType, setPickedType] = useState<CallListPersonType>(givenType);
  const [picked, setPicked] = useState<PickedPerson[]>([]);

  const personType = pickPeople ? pickedType : givenType;
  const ids = pickPeople ? picked.map((person) => person.id) : givenIds;

  useEffect(() => {
    if (!open) return;
    setPicked([]);
    setPickedType(givenType);
    setForOwnerId(defaultOwnerId ?? null);
    setFirstCall(FirstCallWhen.TODAY);
    setFirstCallDate('');
    setNumberChoice(CallNumberType.PHONE);
    setOtherNumber('');
    setReason('');
  }, [open, givenType, defaultOwnerId]);

  useEffect(() => {
    if (!open || !lists?.length) return;
    setListId((current) => current && lists.some((list) => list.id === current)
      ? current
      : (lists.find((list) => list.id === defaultListId) ?? lists[0]).id);
  }, [open, lists, defaultListId]);

  // A Phone field belongs to one kind of record, so switching between contacts and candidates resets it.
  useEffect(() => {
    setNumberChoice(CallNumberType.PHONE);
  }, [personType]);

  const single = ids.length === 1;

  // JT1-1827: warn, never block, when this person is already on a list in the agency.
  const { currentData: memberships } = useGetCallListMembershipsQuery(
    { entityType: personType, entityId: ids[0] },
    { skip: !open || !single || pickPeople }
  );
  const alreadyOn = [
    ...(memberships?.mine ?? []).filter((membership) => membership.status === CallListEntryStatus.ACTIVE)
      .map((membership) => t('call_lists.add.already_on_mine', { list: membership.call_list_name })),
    ...(memberships?.others ?? []).map((shared) => t('call_lists.add.already_on_theirs', { name: `${shared.owner.first_name} ${shared.owner.last_name}`.trim() })),
  ];
  // JT1-1827: each Phone custom field for this kind of record is a number choice, keyed by its config.
  const { currentData: customFieldConfigs } = useGetConfigByEntityTypeQuery(
    { entityType: personType === CallListPersonType.CONTACT ? CustomFieldEntityType.CONTACT : CustomFieldEntityType.CANDIDATE },
    { skip: !open }
  );
  const phoneFields = (customFieldConfigs ?? []).filter((config) => config.field_type === CustomFieldType.PHONE);

  const numberChoices: { key: string; label: string }[] = [
    { key: CallNumberType.PHONE, label: t(`call_lists.numbers.${CallNumberType.PHONE}`) },
    ...phoneFields.map((config) => ({ key: `${CallNumberType.CUSTOM_FIELD}:${config.id}`, label: config.label })),
    ...(personType === CallListPersonType.CONTACT ? [{ key: CallNumberType.SWITCHBOARD, label: t(`call_lists.numbers.${CallNumberType.SWITCHBOARD}`) }] : []),
    { key: CallNumberType.OTHER, label: t(`call_lists.numbers.${CallNumberType.OTHER}`) },
  ];
  const [numberType, customFieldConfigId] = numberChoice.split(':') as [CallNumberType, string | undefined];

  const valid = !!listId
    && ids.length > 0
    && (firstCall !== FirstCallWhen.DATE || !!firstCallDate)
    && (numberType !== CallNumberType.OTHER || !!otherNumber.trim());

  const describeResult = (result: AddCallListEntriesResult) => [
    t('call_lists.add.result.added', { count: result.added + result.reactivated }),
    result.skipped_existing ? t('call_lists.add.result.existing', { count: result.skipped_existing }) : null,
    result.skipped_dnc ? t('call_lists.add.result.dnc', { count: result.skipped_dnc }) : null,
    result.skipped_not_found ? t('call_lists.add.result.not_found', { count: result.skipped_not_found }) : null,
  ].filter(Boolean).join(' · ');

  const submit = async () => {
    if (!listId) return;
    try {
      const result = await addEntries({
        listId,
        data: {
          entity_type: personType,
          entity_ids: ids,
          first_call: firstCall,
          first_call_date: firstCall === FirstCallWhen.DATE ? firstCallDate : undefined,
          dial_number_type: numberType,
          dial_custom_field_config_id: numberType === CallNumberType.CUSTOM_FIELD ? customFieldConfigId : undefined,
          dial_number_other: numberType === CallNumberType.OTHER ? otherNumber.trim() : undefined,
          reason: reason.trim() || undefined,
          added_from: addedFrom,
        },
      }).unwrap();
      const added = result.added + result.reactivated;
      enqueueSnackbar(describeResult(result), { variant: added ? 'success' : 'warning' });
      onSuccess?.(result);
      onClose();
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message || t('call_lists.toasts.action_failed'), { variant: 'error' });
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>{t('call_lists.add.title')}</DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            {alreadyOn.length > 0 && (
              <Alert severity="warning" data-testid="add-to-call-list-already-on">
                {alreadyOn.map((line) => <Box key={line}>{line}</Box>)}
              </Alert>
            )}

            {pickPeople ? (
              <CallListPeoplePicker type={pickedType} onTypeChange={setPickedType} value={picked} onChange={setPicked} />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {single && personName
                  ? `${personName} · ${t(`call_lists.person_types.${personType}`)}`
                  : t(`call_lists.add.selected_${personType}`, { count: ids.length })}
              </Typography>
            )}

            <Stack spacing={1}>
              <Typography variant="subtitle2">{t('call_lists.list')}</Typography>
              {isAdmin && (
                <CallListOwnerPicker value={forOwnerId} onChange={setForOwnerId} label={t('call_lists.owner.for')} />
              )}
              <RadioGroup value={listId ?? ''} onChange={(event) => setListId(event.target.value)}>
                {(lists ?? []).map((list: CallList) => (
                  <FormControlLabel
                    key={list.id}
                    value={list.id}
                    control={<Radio size="small" />}
                    label={(
                      <Stack direction="row" spacing={1} alignItems="baseline">
                        <Typography variant="body2">{list.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('call_lists.people_count', { count: list.counts.active })}
                        </Typography>
                      </Stack>
                    )}
                  />
                ))}
              </RadioGroup>
              <Button size="small" sx={{ alignSelf: 'flex-start' }} startIcon={<Iconify icon="mingcute:add-line" />} onClick={createModal.onTrue}>
                {t('call_lists.new_list.menu')}
              </Button>
              <Typography variant="caption" color="text.secondary">{t('call_lists.add.privacy')}</Typography>
            </Stack>

            <Stack spacing={1}>
              <Typography variant="subtitle2">{t('call_lists.add.first_call')}</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={firstCall}
                  onChange={(_event, value: FirstCallWhen | null) => value && setFirstCall(value)}
                >
                  {FIRST_CALLS.map((option) => (
                    <ToggleButton key={option} value={option}>{t(`call_lists.add.first_calls.${option}`)}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
                {firstCall === FirstCallWhen.DATE && (
                  <TextField
                    type="date"
                    size="small"
                    value={firstCallDate}
                    onChange={(event) => setFirstCallDate(event.target.value)}
                    inputProps={{ min: format(new Date(), 'yyyy-MM-dd'), 'aria-label': t('call_lists.add.first_calls.date') }}
                  />
                )}
              </Stack>
            </Stack>

            <Stack spacing={1}>
              <Typography variant="subtitle2">{t('call_lists.number_to_dial')}</Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={numberChoice}
                onChange={(_event, value: string | null) => value && setNumberChoice(value)}
              >
                {numberChoices.map((choice) => (
                  <ToggleButton key={choice.key} value={choice.key}>{choice.label}</ToggleButton>
                ))}
              </ToggleButtonGroup>
              {numberType === CallNumberType.OTHER && (
                <TextField
                  size="small"
                  label={t('call_lists.numbers.other')}
                  value={otherNumber}
                  onChange={(event) => setOtherNumber(event.target.value)}
                  inputProps={{ maxLength: 50 }}
                />
              )}
              {!single && (
                <Typography variant="caption" color="text.secondary">{t('call_lists.add.number_fallback')}</Typography>
              )}
            </Stack>

            <TextField
              multiline
              minRows={2}
              label={t('call_lists.add.reason')}
              placeholder={t('call_lists.add.reason_placeholder')}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              inputProps={{ maxLength: 500 }}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Box sx={{ flex: 1 }} />
          <LoadingButton variant="contained" loading={isLoading} disabled={!valid} onClick={submit} data-testid="add-to-call-list-submit">
            {t('call_lists.add.submit')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <CallListNameModal open={createModal.value} onClose={createModal.onFalse} ownerId={isAdmin ? forOwnerId : null} onCreated={(list) => setListId(list.id)} />
    </>
  );
}
